/**
 * <PanelBtn>
 *
 * Compact vertical icon + label button used in the <ZoomPanel> controls row
 * (§4 in `docs/ui-designs/06-telehealth-and-scribe.md`). Five of these are
 * stacked horizontally: Mute / Stop Video / Share / Chat / AI Scribe.
 *
 * The button has three visual states:
 *   - idle  → transparent background, slate-400 icon + label
 *   - active → semi-transparent tint of `activeColor` (defaults to white),
 *              icon + label tinted to `activeColor`
 *   - disabled → 40% opacity, no hover effect
 *
 * Hover is handled via Tailwind `hover:` utility. The button intentionally
 * stays minimal — no Mantine wrapping — because we render it inside a
 * dark slate-900 panel where Mantine's default light theming would clash.
 */
import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { scribeText } from './scribeText'

export type PanelBtnProps = {
  /** Inline SVG / Tabler icon node. Sized 18px by default. */
  icon: ReactNode
  /** Short label rendered under the icon. Keep ≤8 characters for layout. */
  label: string
  /** When true, the button paints with `activeColor` and stays in pressed state. */
  active?: boolean
  /** Tint applied when `active`. Defaults to `#e2e8f0` (slate-200). */
  activeColor?: string
  /** Disable click + dim to 40% opacity. */
  disabled?: boolean
  /** Click handler. */
  onClick?: () => void
  /** Accessible label override; falls back to `label`. */
  ariaLabel?: string
  /** Native hover tooltip — used to explain a disabled state. */
  title?: string
  /** Test id for unit tests. */
  testId?: string
  /**
   * Larger pill variant used in the fullscreen controls bar — icon + label
   * inline, bigger padding, larger touch target. The default (compact stack
   * with label below the icon) targets the 272px rail where horizontal room
   * is scarce.
   */
  large?: boolean
}

export function PanelBtn({
  icon,
  label,
  active = false,
  activeColor = '#e2e8f0',
  disabled = false,
  onClick,
  ariaLabel,
  title,
  testId,
  large = false,
}: PanelBtnProps) {
  // In the large pill variant the idle state gets a subtle dark surface so
  // each control reads as a clickable affordance from across the room
  // (fullscreen call-watching context); compact variant stays transparent
  // because the 272px rail wraps the row tightly enough that a background
  // would look noisy.
  // Alpha `22` (≈13.3%) for the active tint matches the mockup at
  // ChartPage.tsx:5273.
  const activeStyle = active
    ? {
        background: large ? `${activeColor}33` : `${activeColor}22`,
        color: activeColor,
        borderColor: large ? `${activeColor}55` : 'transparent',
      }
    : large
      ? { background: '#1e293b', borderColor: '#334155' }
      : undefined

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? label}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      // `min-w-0` + `px-1` keeps the 5-button row inside the 272px default
      // panel width — `px-2` previously inflated each button enough that the
      // last label ("AI Scribe") was clipped at the panel's right edge.
      className={cn(
        'flex items-center justify-center border transition-colors',
        large
          ? 'min-w-[120px] flex-row gap-2 rounded-full px-5 py-2.5'
          : 'min-w-0 flex-col gap-[3px] rounded-md border-transparent px-1 py-[7px]',
        'text-slate-400',
        !disabled && !active && 'cursor-pointer hover:bg-white/[0.08] hover:text-slate-200',
        disabled && 'cursor-not-allowed opacity-40',
      )}
      style={activeStyle}
    >
      <span aria-hidden="true" className="flex items-center justify-center">
        {icon}
      </span>
      <span
        className={
          large
            ? `${scribeText.label} leading-none whitespace-nowrap`
            : `${scribeText.micro} leading-none font-medium whitespace-nowrap`
        }
      >
        {label}
      </span>
    </button>
  )
}
